import React, { createContext, useContext, useEffect, useState } from "react";
import {
  User,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut,
  getIdToken,
  sendEmailVerification,
} from "firebase/auth";
import { auth } from "../lib/firebase";
import { loginWithFirebaseToken, logoutBackend } from "../services/backendAuth";
import { clearTokens, getRefresh } from "../lib/tokenStorage";
import { onForceLogout } from "../lib/authEvents";
import toast from "react-hot-toast";

type Ctx = {
  user: User | null;
  loading: boolean;
  loginEmail: (email: string, pw: string, remember: boolean) => Promise<void>;
  loginGoogle: () => Promise<void>;
  loginFacebook: () => Promise<void>;
  forgot: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  signup: (fullName: string, email: string, pw: string) => Promise<void>;
};

const AuthCtx = createContext<Ctx>({} as Ctx);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    const off = onForceLogout(() => {
      void (async () => {
        try {
          const refresh = getRefresh();
          await logoutBackend(refresh || undefined);
        } catch {
        } finally {
          clearTokens();
          try {
            await signOut(auth);
          } catch {
          }
        }
      })();
    });
    return off;
  }, []);

  const loginEmail = async (email: string, pw: string, remember: boolean) => {
    await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);
    const cred = await signInWithEmailAndPassword(auth, email, pw);

    if (!cred.user.emailVerified) {
      try {
        await sendEmailVerification(cred.user, {
          url: `${window.location.origin}/login?verified=1`,
          handleCodeInApp: false,
        });
      } catch {
        // console.log("Failed to send verification email");
        toast.error("Failed to send verification email");
       }
      await signOut(auth);
      throw new Error("Your email isn’t verified yet. We’ve sent you a verification link.");
    }

    const idToken =
      (cred as any)?._tokenResponse?.idToken ?? (await getIdToken(cred.user, true));
    await loginWithFirebaseToken(idToken);
  };

  const loginGoogle = async () => {
    const cred = await signInWithPopup(auth, new GoogleAuthProvider());
    const idToken = await getIdToken(cred.user, true);
    await loginWithFirebaseToken(idToken);
  };

  const loginFacebook = async () => {
    const cred = await signInWithPopup(auth, new FacebookAuthProvider());
    const idToken = await getIdToken(cred.user, true);
    await loginWithFirebaseToken(idToken);
  };

  const forgot = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const logout = async () => {
    const refresh: any = getRefresh();
    try {
      await logoutBackend(refresh);
    } finally {
      clearTokens();
      await signOut(auth);
    }
  };

 const signup = async (fullName: string, email: string, pw: string) => {
  const cred = await createUserWithEmailAndPassword(auth, email, pw);
  if (fullName) await updateProfile(cred.user, { displayName: fullName });

  await sendEmailVerification(cred.user, {
    url: `${window.location.origin}/login?verified=1`,
    handleCodeInApp: false,
  });

  await signOut(auth);

};

  return (
    <AuthCtx.Provider
      value={{ user, loading, loginEmail, loginGoogle, loginFacebook, forgot, logout, signup }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
